import type {
  PortalAssetClientCategory,
  PortalAssetCounts,
  PortalAssetDetail,
  PortalAssetEnrichmentSuggestion,
  PortalAssetAiField,
  PortalAssetHistoryEntry,
  PortalAssetLifecycle,
  PortalAssetListResponse,
  PortalAssetMediaType,
  PortalAssetReviewStatus,
  PortalAssetTile,
  PortalAssetUploadRequest,
  PortalAssetUploadResponse,
} from '../../types/portalAsset';
import type { SortOptionId } from '../../lib/portalAssets/sortOptions';
import type { BulkOperation, BulkResponse, ClearPreview } from '../../lib/portalAssets/bulk';

// THE SEAM BETWEEN THE SHARED ASSET COMPONENTS AND THE TWO IDENTITY POOLS.
//
// ⚠️⚠️ The grid, upload and detail components are shared by the client portal
// and the staff app (spec §8: "one grid, two hosts"), but a CLIENT page must only
// ever call /api/portal/assets and a STAFF page only /api/portal-assets. So the
// components know NO endpoint: each host passes an implementation of this
// interface. The client one lives in src/modules/clientPortal/portal/assets/ and
// is the only file here that names /api/portal/; nothing in this module may.

export type AssetListParams = {
  /** Client: optional (absent = the whole operational scope). Staff: required. */
  accountId?: string;
  /** ⚠️ STAFF ONLY — the client route refuses `view` (Recently deleted is Kyck's). */
  view?: 'live' | 'deleted';
  status?: PortalAssetReviewStatus;
  mediaType?: PortalAssetMediaType;
  lifecycle?: PortalAssetLifecycle;
  tag?: string;
  /** One category document / one category type — never both. */
  categoryId?: string;
  categoryKind?: string;
  /** One of the four named sorts (sortOptions.ts). */
  sort?: SortOptionId;
  /** ⚠️ Search resolves with the scope alone: the server refuses it beside any other filter or a non-default sort. */
  search?: string;
  /** ⚠️ STAFF ONLY ("Not reviewed by Kyck"): the client route refuses it. Scope alone, like search. */
  kyckUnreviewed?: boolean;
  /** ISO instants; `to` is exclusive. Only with a date sort. */
  from?: string;
  to?: string;
  cursor?: string;
  limit?: number;
};

/** ⚠️ `categoryIds` REPLACES the asset's set. There is no `categoryKinds`: the server derives it. */
export type AssetMetadataPatch = {
  categoryIds?: string[];
  tags?: string[];
  /** A string sets the title; null clears it. */
  title?: string | null;
  /**
   * ⚠️ A RECEIPT, never a provenance. Present the one `enrich` returned plus which fields the user left exactly as
   * suggested; the server verifies it and decides what was AI-written.
   */
  aiSuggestion?: { receipt: string; untouched: PortalAssetAiField[] };
};

/**
 * THE BULK SEAM (Run F). The shared bulk bar names no endpoint: staff pass staffApi's, a club passes its own. ⚠️ `clearPreview`
 * is STAFF ONLY and its absence IS the switch: a host that supplies none gets no Clear button at all (Clear has no recovery).
 * Results are 200 per asset even when some fail: read `results`, never the status.
 */
export type PortalAssetsBulkApi = {
  run(assetIds: readonly string[], operation: BulkOperation): Promise<BulkResponse>;
  enrich(assetIds: readonly string[]): Promise<BulkResponse>;
  clearPreview?(assetIds: readonly string[]): Promise<{ preview: ClearPreview }>;
};

export type PortalAssetsApi = {
  /** Distinguishes the two hosts' react-query caches. */
  cacheKey: 'portal' | 'staff';
  list(params: AssetListParams): Promise<PortalAssetListResponse>;
  detail(assetId: string): Promise<PortalAssetDetail>;
  patch(assetId: string, patch: AssetMetadataPatch): Promise<PortalAssetTile>;
  /** AI suggestion for one asset (Run D). Writes NOTHING; costs tokens, so only ever called from a click. Both hosts. */
  enrich(assetId: string): Promise<PortalAssetEnrichmentSuggestion>;
  /** Soft-delete. Both hosts may; only staff may restore (staffApi.ts restorePortalAsset). */
  remove(assetId: string): Promise<PortalAssetTile>;
  /** Approve / not approve. BOTH hosts since 2026-10-08: a club and Kyck are equal authorities. */
  review(assetId: string, input: { decision: 'approved' | 'rejected'; reason?: string | null }): Promise<PortalAssetTile>;
  /** The asset's decision history (who decided what), from its audit entries. */
  history(assetId: string): Promise<{ entries: PortalAssetHistoryEntry[] }>;
  counts(accountId: string | undefined): Promise<{ counts: PortalAssetCounts[] }>;
  /**
   * The categories an ACCOUNT's assets may be filed under. ⚠️ The brand is derived
   * server-side from the account; neither host sends a brand id.
   */
  categories(accountId: string): Promise<{ categories: PortalAssetClientCategory[] }>;
  createUploads(req: PortalAssetUploadRequest): Promise<PortalAssetUploadResponse>;
  complete(assetId: string): Promise<{ lifecycle: PortalAssetLifecycle }>;
};

/** The list query string. Only defined values; the server refuses unknown keys. */
export function listQueryString(p: AssetListParams): string {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(p)) {
    if (v === undefined || v === '' || v === false) continue;
    q.set(k, String(v));
  }
  const s = q.toString();
  return s ? `?${s}` : '';
}
